'use client'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useRouter } from '@bprogress/next/app'
import { useLocale, useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import Flag from 'react-flagkit'
import { toast } from 'sonner'
import { formatDateTime, getCategoryPath, getCountryCodeFromLocale, getLocalizedValue } from '@/lib/utils'
import {
  MoreHorizontalIcon,
  EyeIcon,
  EditIcon,
  Trash2Icon,
  ArrowLeftRightIcon,
  CalendarIcon,
  HashIcon,
  SquarePlusIcon,
  SquareMenuIcon,
  ListTreeIcon,
  CheckIcon,
  XIcon,
  FolderTreeIcon,
  RefreshCwIcon
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { CategoriesFilters } from '@/components/partials/categories'
import { LoadingState, SortableHeader, DeleteConfirmationDialog, TableSortActions } from '@/components/partials'
import { CategoriesFilterParams } from '@/types/category'
import { BulkDeleteResponse } from '@/types/general'
import { useCategories, useSortCategory, useBulkDeleteCategories, useDeleteCategory } from '@/hooks'
import { useCategoriesStore, useMetaStore, useMeStore } from '@/store'
import { hasPermission } from '@/lib/permissions'
import { SortEnum, SortOrderEnum } from '@/enums'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)
  const { mutate: sortLocale, isPending: isSorting } = useSortCategory()
  const { mutate: bulkDeleteCategories, isPending: isBulkDeleting } = useBulkDeleteCategories()
  const { mutate: deleteCategoryMutate, isPending: isDeleting } = useDeleteCategory()

  const { metaState } = useMetaStore()
  const { meState } = useMeStore()
  const currentUser = meState.response
  const canCreate = hasPermission(currentUser, 'CATEGORIES', 'CREATE')
  const canShow = hasPermission(currentUser, 'CATEGORIES', 'SHOW')
  const canUpdate = hasPermission(currentUser, 'CATEGORIES', 'UPDATE')
  const canDelete = hasPermission(currentUser, 'CATEGORIES', 'DELETE')
  const defaultLocale = metaState.response?.generalSettings.adminDefaultLocale
  const selectedCategoryIds = useCategoriesStore(state => state.selectedCategoryIds)
  const toggleCategorySelection = useCategoriesStore(state => state.toggleCategorySelection)
  const selectAllCategories = useCategoriesStore(state => state.selectAllCategories)
  const deselectAllCategories = useCategoriesStore(state => state.deselectAllCategories)

  const filters = useMemo<CategoriesFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.ASC,
      sortBy: params.get('sortBy') || 'left',
      q: params.get('q') || '',
      active: params.get('active') ?? undefined,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])

  const { data: categoriesData, isLoading, error, refetch, isFetching } = useCategories(filters)

  // URL params handling
  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString())
    const newFilters: CategoriesFilterParams = {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.ASC,
      sortBy: params.get('sortBy') || 'left'
    }

    if (params.get('q')) {
      newFilters.q = params.get('q') || ''
    }
    if (params.get('createdAtStart')) {
      newFilters.createdAtStart = params.get('createdAtStart') || undefined
    }
    if (params.get('createdAtEnd')) {
      newFilters.createdAtEnd = params.get('createdAtEnd') || undefined
    }
    if (params.get('updatedAtStart')) {
      newFilters.updatedAtStart = params.get('updatedAtStart') || undefined
    }
    if (params.get('updatedAtEnd')) {
      newFilters.updatedAtEnd = params.get('updatedAtEnd') || undefined
    }
    if (params.get('active')) {
      newFilters.active = params.get('active') || undefined
    }
  }, [searchParams])

  // Update URL when filters change
  const updateUrlParams = (newFilters: Partial<CategoriesFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value.toString())
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<CategoriesFilterParams>) => {
    updateUrlParams(newFilters)
  }

  const handleResetFilters = () => {
    router.push('/products/categories')
  }

  const handleSort = (sortBy: string, sort: SortEnum) => {
    handleFiltersChange({ sortBy, sort, page: 1 })
  }

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleView = (id: string) => {
    router.push(`/products/categories/${id}`)
  }

  const handleEdit = (id: string) => {
    router.push(`/products/categories/${id}/edit`)
  }

  const handleDelete = (id: string) => {
    setDeleteId(id)
  }

  const confirmDelete = () => {
    if (deleteId) {
      deleteCategoryMutate(deleteId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['categories'] })
          toast.success(t('deleted_successfully'))
          setDeleteId(null)
        },
        onError: error => {
          console.error('Error deleting category:', error)
          toast.error(t('error_deleting'))
          setDeleteId(null)
        }
      })
    }
  }

  const handleBulkDelete = () => {
    if (selectedCategoryIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const confirmBulkDelete = () => {
    if (selectedCategoryIds.length > 0) {
      bulkDeleteCategories(
        { ids: selectedCategoryIds },
        {
          onSuccess: async (response: BulkDeleteResponse) => {
            await queryClient.invalidateQueries({ queryKey: ['categories'] })
            if (response.deletedItems && response.deletedItems.length > 0) {
              toast.success(t('deleted_successfully'))
            }
            setBulkDeleteConfirmOpen(false)
            deselectAllCategories()
          },
          onError: (error: any) => {
            if (error.response) {
              if (error.response.status === 422) {
                toast.error(error.response.data?.items?.ids || t('error_deleting'))
              } else {
                toast.error(t('error_deleting'))
              }
            } else {
              toast.error(t('error_deleting'))
            }

            setBulkDeleteConfirmOpen(false)
          }
        }
      )
    }
  }

  const handleSelectAll = () => {
    if (categoriesData && categoriesData.items.length > 0) {
      if (selectedCategoryIds.length === categoriesData.items.length) {
        // If all categories are selected, deselect all
        deselectAllCategories()
      } else {
        // Otherwise, select all categories
        selectAllCategories(categoriesData.items)
      }
    }
  }

  const handleSortOrder = (id: string, type: SortOrderEnum) => {
    sortLocale(
      { categoryId: id, type: type },
      {
        onSuccess: async updatedCategory => {
          queryClient.setQueryData(['category', id], updatedCategory)
          await queryClient.invalidateQueries({ queryKey: ['categories'] })
          toast.success(t('sorted_successfully'))
        },
        onError: error => {
          console.error('Error sorting locale:', error)
          toast.error(t('failed_to_sort'))
        }
      }
    )
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FolderTreeIcon className="h-5 w-5" />
                {t('categories')}
              </CardTitle>
              <CardDescription>{t('categories_description')}</CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {canCreate && (
                <Button asChild>
                  <Link href="/products/categories/create">
                    <SquarePlusIcon />
                    <span className="hidden flex-row items-center justify-items-center lg:flex">
                      {t('create_category')}
                    </span>
                  </Link>
                </Button>
              )}
              <Button variant="outline" asChild>
                <Link href="/products/categories/tree">
                  <ListTreeIcon />
                  <span className="hidden flex-row items-center justify-items-center lg:flex">{t('tree_view')}</span>
                </Link>
              </Button>
              {canDelete && selectedCategoryIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedCategoryIds.length})</span>
                  </div>
                </Button>
              )}
              <CategoriesFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {categoriesData && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          <div className="flex items-center justify-center">
                            {canDelete && (
                              <Checkbox
                                checked={
                                  categoriesData &&
                                  categoriesData.items.length > 0 &&
                                  selectedCategoryIds.length === categoriesData.items.length
                                }
                                onCheckedChange={() => {
                                  handleSelectAll()
                                }}
                                onClick={e => {
                                  e.stopPropagation()
                                }}
                                className="cursor-pointer"
                              />
                            )}
                          </div>
                        </TableHead>
                        <TableHead className="w-25">
                          <SortableHeader
                            sortBy="id"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <HashIcon className="mr-2 h-4 w-4" />
                            ID
                          </SortableHeader>
                        </TableHead>
                        <TableHead>
                          <SortableHeader
                            sortBy={`translations.${locale}.name`}
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <div className="flex items-center gap-2">
                              <Flag country={getCountryCodeFromLocale(locale)} size={16} />
                              {t('name')}
                            </div>
                          </SortableHeader>
                        </TableHead>
                        <TableHead>
                          <SortableHeader
                            sortBy={`translations.${locale}.slug`}
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <div className="flex items-center gap-2">
                              <Flag country={getCountryCodeFromLocale(locale)} size={16} />
                              {t('slug')}
                            </div>
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="text-right">
                          <SortableHeader
                            sortBy="left"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <ArrowLeftRightIcon className="mr-2 h-4 w-4" />
                            {t('position')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-25 text-center">
                          <SortableHeader
                            sortBy="active"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CheckIcon className="h-4 w-4" />
                            {t('active')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-37.5">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {t('created_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-37.5">
                          <SortableHeader
                            sortBy="updatedAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {t('updated_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-20">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categoriesData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        categoriesData.items.map((category, index) => (
                          <ContextMenu key={category.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => canShow && handleView(category.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    {canDelete && (
                                      <Checkbox
                                        checked={selectedCategoryIds.includes(category.id)}
                                        onCheckedChange={() => toggleCategorySelection(category.id)}
                                        onClick={e => {
                                          e.stopPropagation()
                                        }}
                                        className="cursor-pointer"
                                      />
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-muted-foreground font-mono text-xs">{category.id}</TableCell>
                                <TableCell>
                                  <div className="max-w-75">
                                    <div className="truncate font-medium">
                                      {getLocalizedValue(category, 'name', locale, defaultLocale)}
                                    </div>
                                    <div className="text-muted-foreground truncate text-xs">
                                      {getCategoryPath(category, locale, defaultLocale)}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="max-w-75">
                                    <div className="truncate font-medium">
                                      {getLocalizedValue(category, 'slug', locale, defaultLocale)}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="flex flex-row items-center justify-end gap-1">
                                  <Badge variant="outline" className="font-mono text-xs">
                                    {category.left} - {category.right}
                                  </Badge>
                                  {canUpdate && (
                                    <TableSortActions
                                      onSortUp={() => handleSortOrder(category.id, SortOrderEnum.UP)}
                                      onSortDown={() => handleSortOrder(category.id, SortOrderEnum.DOWN)}
                                      disabled={isSorting}
                                    />
                                  )}
                                </TableCell>
                                <TableCell className="text-center">
                                  {category.active ? (
                                    <CheckIcon className="mx-auto h-5 w-5 text-green-500" />
                                  ) : (
                                    <XIcon className="mx-auto h-5 w-5 text-red-500" />
                                  )}
                                </TableCell>
                                <TableCell className="text-muted-foreground text-center text-sm">
                                  {formatDateTime(category.createdAt, locale)}
                                </TableCell>
                                <TableCell className="text-muted-foreground text-center text-sm">
                                  {formatDateTime(category.updatedAt, locale)}
                                </TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        className="h-8 w-8 p-0"
                                        onClick={e => e.stopPropagation()}>
                                        <span className="sr-only">{t('open_menu')}</span>
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {canShow && (
                                        <DropdownMenuItem
                                          onClick={e => {
                                            e.stopPropagation()
                                            handleView(category.id)
                                          }}>
                                          <EyeIcon className="mr-2 h-4 w-4" />
                                          {t('category_details')}
                                        </DropdownMenuItem>
                                      )}

                                      {canUpdate && (
                                        <DropdownMenuItem
                                          onClick={e => {
                                            e.stopPropagation()
                                            handleEdit(category.id)
                                          }}>
                                          <EditIcon className="mr-2 h-4 w-4" />
                                          {t('edit_category')}
                                        </DropdownMenuItem>
                                      )}

                                      {canDelete && (
                                        <DropdownMenuItem
                                          onClick={e => {
                                            e.stopPropagation()
                                            handleDelete(category.id)
                                          }}
                                          className="text-destructive focus:text-destructive">
                                          <Trash2Icon className="mr-2 h-4 w-4" />
                                          {t('delete')}
                                        </DropdownMenuItem>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>
                              {canShow && (
                                <ContextMenuItem
                                  onClick={e => {
                                    e.stopPropagation()
                                    handleView(category.id)
                                  }}>
                                  <EyeIcon className="mr-2 h-4 w-4" />
                                  {t('category_details')}
                                </ContextMenuItem>
                              )}

                              {canUpdate && (
                                <ContextMenuItem
                                  onClick={e => {
                                    e.stopPropagation()
                                    handleEdit(category.id)
                                  }}>
                                  <EditIcon className="mr-2 h-4 w-4" />
                                  {t('edit_category')}
                                </ContextMenuItem>
                              )}

                              {canDelete && (
                                <ContextMenuItem
                                  onClick={e => {
                                    e.stopPropagation()
                                    handleDelete(category.id)
                                  }}
                                  className="text-destructive focus:text-destructive">
                                  <Trash2Icon className="mr-2 h-4 w-4" />
                                  {t('delete')}
                                </ContextMenuItem>
                              )}
                            </ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={categoriesData.page}
                  totalPages={categoriesData.pages}
                  totalItems={categoriesData.total}
                  pageSize={categoriesData.size}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      {canDelete && (
        <DeleteConfirmationDialog
          open={deleteId !== null}
          onCancel={() => setDeleteId(null)}
          onConfirm={confirmDelete}
          isDeleting={isDeleting}
        />
      )}

      {canDelete && (
        <DeleteConfirmationDialog
          open={bulkDeleteConfirmOpen}
          onCancel={() => setBulkDeleteConfirmOpen(false)}
          onConfirm={confirmBulkDelete}
          isDeleting={isBulkDeleting}
        />
      )}
    </>
  )
}

export default List
